/**
 * ComboboxFKMulti — Multi-select searchable dropdown for FK fields
 * Extends ComboboxFK with ability to select multiple items
 *
 * Usage:
 *   const { options, isLoading } = useCustomerLookup();
 *   <ComboboxFKMulti
 *     value={selectedIds}
 *     onChange={setSelectedIds}
 *     options={options}
 *     label="Khách hàng"
 *   />
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { Check, ChevronsUpDown, Search, Loader2, X, ChevronDown, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn, normalizeVi, fuzzyMatchVi } from '@/lib/utils';
import type { FKOption } from '@/hooks/useFKLookup';

interface ComboboxFKMultiProps {
    value: string[];
    onChange: (ids: string[]) => void;
    options: FKOption[];
    label?: string;
    placeholder?: string;
    required?: boolean;
    loading?: boolean;
    error?: boolean;
    disabled?: boolean;
    onNameChange?: (names: string[]) => void;
    hideLabel?: boolean;
    canAdd?: boolean;
    quickAddLabel?: string;
    onQuickAddClick?: (name: string) => void;
}

export function ComboboxFKMulti({
    value,
    onChange,
    options,
    label,
    placeholder = 'Chọn...',
    required,
    loading,
    error,
    disabled,
    onNameChange,
    hideLabel,
    canAdd,
    quickAddLabel,
    onQuickAddClick,
}: ComboboxFKMultiProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [displayLimit, setDisplayLimit] = useState(50);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => searchInputRef.current?.focus(), 50);
            setDisplayLimit(50);
        } else {
            setSearch('');
        }
    }, [open]);

    // Filter options by search
    const filtered = useMemo(() => {
        if (!search.trim()) return options;
        return options.filter(o => {
            if (fuzzyMatchVi(o.searchTags, search)) return true;
            if (fuzzyMatchVi(o.label, search)) return true;
            if (fuzzyMatchVi(o.subLabel, search)) return true;
            if (fuzzyMatchVi(o.meta, search)) return true;
            return false;
        });
    }, [options, search]);

    // Find selected options
    const selectedOptions = options.filter(o => value.some(v => String(v) === String(o.id)));

    const handleSelect = (optionId: string) => {
        const idStr = String(optionId);
        const newValue = value.some(v => String(v) === idStr)
            ? value.filter(v => String(v) !== idStr)
            : [...value, idStr];
        onChange(newValue);
        if (onNameChange) {
            const names = options
                .filter(o => newValue.some(v => String(v) === String(o.id)))
                .map(o => o.label);
            onNameChange(names);
        }
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
        if (onNameChange) onNameChange([]);
    };

    const handleRemove = (id: string, e: React.MouseEvent) => {
        e.preventDefault();
        const idStr = String(id);
        const newValue = value.filter(v => String(v) !== idStr);
        onChange(newValue);
        if (onNameChange) {
            const names = options
                .filter(o => newValue.some(v => String(v) === String(o.id)))
                .map(o => o.label);
            onNameChange(names);
        }
    };

    const handleShowMore = useCallback(() => {
        setDisplayLimit(prev => prev + 50);
    }, []);

    return (
        <div className={cn("space-y-1.5", hideLabel && "space-y-0")}>
            {!hideLabel && label && (
                <Label className="text-xs font-medium text-slate-700">
                    {label}
                    {required && <span className="text-destructive ml-0.5">*</span>}
                </Label>
            )}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        className={cn(
                            'w-full justify-between h-auto min-h-9 text-sm font-normal',
                            !value.length && 'text-muted-foreground',
                            error && 'border-destructive',
                            'p-1.5'
                        )}
                    >
                        <div className="flex flex-wrap gap-1 flex-1 text-left">
                            {loading ? (
                                <span className="flex items-center gap-1.5 text-slate-400">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Đang tải...
                                </span>
                            ) : value.length > 0 ? (
                                selectedOptions.map(opt => (
                                    <Badge
                                        key={opt.id}
                                        variant="secondary"
                                        className="flex items-center gap-1 text-xs"
                                    >
                                        {opt.label}
                                        <X
                                            className="h-2.5 w-2.5 cursor-pointer hover:text-destructive"
                                            onClick={(e) => handleRemove(opt.id, e)}
                                        />
                                    </Badge>
                                ))
                            ) : (
                                <span className="text-muted-foreground">{placeholder}</span>
                            )}
                        </div>
                        <div className="flex items-center gap-0.5 ml-1 shrink-0">
                            {value.length > 0 && !disabled && (
                                <X
                                    className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                                    onClick={handleClear}
                                />
                            )}
                            <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
                        </div>
                    </Button>
                </PopoverTrigger>

                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
                    align="start"
                >
                    {/* Search */}
                    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b">
                        <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <Input
                            ref={searchInputRef}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder={`Tìm ${label ? label.toLowerCase() : '...'}...`}
                            className="h-7 text-xs border-0 shadow-none focus-visible:ring-0 px-0"
                        />
                    </div>

                    {/* Options list */}
                    <div className="max-h-[280px] overflow-y-auto">
                        {filtered.length === 0 ? (
                            <div className="px-3 py-4 text-center text-xs text-slate-400">
                                Không tìm thấy kết quả
                            </div>
                        ) : (
                            <>
                                {filtered.slice(0, displayLimit).map(option => (
                                    <button
                                        key={option.id}
                                        type="button"
                                        className={cn(
                                            'w-full flex items-start gap-2 px-2 py-2 text-left text-xs hover:bg-slate-50 transition-colors',
                                            value.some(v => String(v) === String(option.id)) && 'apv-accent-bg-soft',
                                        )}
                                        onClick={() => handleSelect(option.id)}
                                    >
                                        <Check
                                            className={cn(
                                                'h-3.5 w-3.5 shrink-0 mt-0.5',
                                                value.some(v => String(v) === String(option.id)) ? 'apv-accent-text' : 'invisible',
                                            )}
                                        />
                                        <div className="flex-1 min-w-0 flex items-start gap-2">
                                            {option.thumbnail && (
                                                <img src={option.thumbnail} alt="" className="w-6 h-6 rounded object-cover shrink-0 border border-slate-200 mt-px" />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start gap-1.5 flex-wrap">
                                                    {option.subLabel && (
                                                        <span className="font-mono text-[10px] text-indigo-600 bg-indigo-50 px-1 rounded shrink-0 mt-px">
                                                            {option.subLabel}
                                                        </span>
                                                    )}
                                                    <span className="text-slate-900 font-medium leading-tight break-words">{option.label}</span>
                                                </div>
                                                {option.meta && (
                                                    <div className="text-[10px] text-slate-500 mt-0.5 leading-tight break-words">
                                                        {option.meta}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                ))}
                                {filtered.length > displayLimit && (
                                    <button
                                        type="button"
                                        onClick={handleShowMore}
                                        className="w-full px-3 py-2 text-center text-[11px] text-blue-600 hover:bg-blue-50 border-t border-dashed transition-colors flex items-center justify-center gap-1"
                                    >
                                        <ChevronDown className="h-3 w-3" />
                                        Xem thêm ({filtered.length - displayLimit} còn lại)
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                    {canAdd && onQuickAddClick && (
                        <div className="p-2 border-t mt-1">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full h-8 text-xs font-semibold apv-accent-text hover:apv-accent-bg-hover"
                                onClick={() => {
                                    setOpen(false);
                                    onQuickAddClick(search);
                                }}
                            >
                                <Plus className="h-3.5 w-3.5 mr-1.5" />
                                {quickAddLabel || 'Thêm nhanh'}
                                {search && <span className="ml-1 opacity-75 truncate max-w-[150px]">"{search}"</span>}
                            </Button>
                        </div>
                    )}
                </PopoverContent>
            </Popover>
        </div>
    );
}
